import { useState, useRef } from 'react'
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom'
import ReCAPTCHA from 'react-google-recaptcha'
import { RECAPTCHA_SITE_KEY } from '../lib/api-config'

import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Link,
  Modal,
  ModalOverlay,
  Spinner,
  Stack,
  Text
} from '@chakra-ui/react'
import { ArrowBackIcon, InfoOutlineIcon } from '@chakra-ui/icons'

import { userMaintenance } from '../lib/api-helper'

import Page from '../components/page'
import LabeledInput from '../components/styled/labeled-input'
import StyledModalContent from '../components/styled/modal-content'
import { useRanContext } from '../components/ran-state-provider'

import backgroundOutline from '../assets/images/background-outline.png'
import logo from '../assets/images/logo.png'

// These rules mirror the ones the server checks (RegisterDTO), so the user is
// told about a problem while typing instead of after a failed submit.
const WORD_ONLY = /^[A-Za-z0-9_]+$/
const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const checkWord = (value, label) => {
  if (!value) return `${label} is required.`
  if (value.length < 4 || value.length > 11)
    return `${label} must be 4 to 11 characters long.`
  if (!WORD_ONLY.test(value))
    return `${label} can only contain letters, numbers and underscore ( _ ).`
  return ''
}

const checkEmail = email => {
  if (!email) return 'Email address is required.'
  if (!EMAIL_FORMAT.test(email))
    return 'That does not look like an email address. Example: juan@gmail.com'
  if (email.length > 50) return 'Email address must be 50 characters or less.'
  return ''
}

const checkMatch = (value, original, label, originalLabel) => {
  if (!value) return `Please type your ${originalLabel} again.`
  if (value !== original)
    return `${label} does not match your ${originalLabel}.`
  return ''
}

const validators = {
  username: v => checkWord(v.username, 'Username'),
  password: v => checkWord(v.password, 'Password'),
  confirmPassword: v =>
    checkMatch(v.confirmPassword, v.password, 'This', 'password'),
  email: v => checkEmail(v.email),
  pinCode: v => checkWord(v.pinCode, 'Pin code'),
  confirmPinCode: v =>
    checkMatch(v.confirmPinCode, v.pinCode, 'This', 'pin code'),
  securityKey: v => checkWord(v.securityKey, 'Security key'),
  referralCode: v =>
    v.referralCode && v.referralCode.length > 15
      ? 'Referral code must be 15 characters or less.'
      : ''
}

// Which fields belong to which step of the form.
const STEP_FIELDS = [
  ['username', 'password', 'confirmPassword', 'email'],
  ['pinCode', 'confirmPinCode', 'securityKey', 'referralCode']
]

const Register = () => {
  const navigate = useNavigate()
  const { isAuthenticated, setAuthentication, setToast } = useRanContext()

  const [page, setPage] = useState(0)
  const [isLoading, setLoading] = useState(false)
  const [touched, setTouched] = useState({})
  const [inputVal, setInputVal] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    pinCode: '',
    confirmPinCode: '',
    securityKey: '',
    referralCode: ''
  })

  const recaptchaRef = useRef(null)

  const onError = msg => {
    setToast({
      status: 'error',
      messages: [msg].flat()
    })
  }

  const setField = (field, value) => {
    setInputVal(prev => ({ ...prev, [field]: value }))
  }

  const touchField = field => setTouched(prev => ({ ...prev, [field]: true }))

  // A field only complains once the user has left it or tried to move on,
  // so the form does not look broken before it has even been filled in.
  const errorOf = field => (touched[field] ? validators[field](inputVal) : '')

  // Shared props for every field: keeps the JSX below short and consistent.
  const fieldProps = (field, onEnter) => ({
    value: inputVal[field],
    error: errorOf(field),
    onChange: e => setField(field, e.target.value),
    onBlur: () => touchField(field),
    onEnter
  })

  const isStepValid = step =>
    STEP_FIELDS[step].every(f => !validators[f](inputVal))

  const revealStepErrors = step => {
    setTouched(prev => {
      const next = { ...prev }
      STEP_FIELDS[step].forEach(f => (next[f] = true))
      return next
    })
    onError('Please fix the highlighted fields below.')
  }

  const goToNextStep = () => {
    if (!isStepValid(0)) return revealStepErrors(0)
    setPage(1)
  }

  const handleSubmit = async () => {
    if (isLoading) return
    if (!isStepValid(1)) return revealStepErrors(1)

    setLoading(true)

    try {
      const captchaToken = await recaptchaRef.current.executeAsync()
      recaptchaRef.current.reset()

      const payload = {
        ...inputVal,
        captchaToken
      }

      const result = await userMaintenance(
        'REGISTER',
        payload,
        setAuthentication
      )

      if (result?.success) {
        setToast({
          status: 'success',
          messages: ['Account has been successfully created']
        })
        navigate('/login')
        return
      }

      onError(
        result?.errors || [
          'We could not create your account right now. Please try again.'
        ]
      )
    } catch (error) {
      onError(
        'We could not reach the server. Please check your internet connection and try again.'
      )
    }

    setLoading(false)
  }

  const RegisterHeader = () => (
    <Heading variant="main" fontSize={[32]} fontWeight={800} mb={5}>
      SERVER INFO
    </Heading>
  )

  const StepIndicator = () => (
    <Box w="full" mb={4}>
      <Text fontSize={11} letterSpacing={2} fontWeight={700} mb={2}>
        STEP {page + 1} OF 2 —{' '}
        {page === 0 ? 'LOGIN DETAILS' : 'ACCOUNT SECURITY'}
      </Text>
      <Flex gap={2}>
        <Box flex={1} h="3px" bg="maroon" />
        <Box flex={1} h="3px" bg={page === 1 ? 'maroon' : '#0e0e0e25'} />
      </Flex>
    </Box>
  )

  return isAuthenticated ? (
    <Navigate to="/account" replace={true} />
  ) : (
    <>
      <Page header={<RegisterHeader />} narrowProfile highProfile>
        <Modal isOpen={true} isCentered>
          <ModalOverlay
            bgImage={backgroundOutline}
            bgPosition="center"
            bgSize="cover"
          />
          <StyledModalContent
            display="flex"
            justifyContent="space-between"
            h={['calc(90vh - 60px)', 'calc(100vh - 60px)']}
            minH={600}
            maxH={840}
            py={['30px', '40px']}
            px={[6, 37]}
          >
            <Flex
              flexDirection="column"
              align="center"
              w="full"
              flex="1"
              minH={0}
              overflowY="auto"
            >
              <Link
                as={RouterLink}
                to="/"
                position="absolute"
                w="full"
                fontSize={14}
                pl={[6, 37]}
                opacity={0.8}
                _hover={{ opacity: 1 }}
              >
                <ArrowBackIcon />
                Home
              </Link>
              <Image src={logo} maxW={110} />

              {page === 0 ? (
                <>
                  <Heading fontSize={24} mt={3} mb={2} textAlign="center">
                    Create an account
                  </Heading>
                  <Text fontSize={13} textAlign="center" opacity={0.7} mb={4}>
                    First, choose how you will log in.
                  </Text>
                  <StepIndicator />
                  <LabeledInput
                    label="USERNAME"
                    hint="The name you use to log in. 4 to 11 characters, letters, numbers and _ only."
                    placeholder="e.g. juan_dela01"
                    maxLength={11}
                    autoComplete="username"
                    {...fieldProps('username', goToNextStep)}
                  />
                  <LabeledInput
                    label="PASSWORD"
                    hint="4 to 11 characters, letters, numbers and _ only. Tap SHOW to check what you typed."
                    placeholder="Your login password"
                    maxLength={11}
                    autoComplete="new-password"
                    isSecret
                    {...fieldProps('password', goToNextStep)}
                  />
                  <LabeledInput
                    label="CONFIRM PASSWORD"
                    hint="Type the same password again so we know there is no typo."
                    placeholder="Repeat your password"
                    maxLength={11}
                    autoComplete="new-password"
                    isSecret
                    {...fieldProps('confirmPassword', goToNextStep)}
                  />
                  <LabeledInput
                    label="EMAIL ADDRESS"
                    hint="Use a real email — your verification is sent here, and it is how you recover a forgotten password or pin code."
                    placeholder="e.g. juan@gmail.com"
                    type="email"
                    maxLength={50}
                    autoComplete="email"
                    {...fieldProps('email', goToNextStep)}
                  />
                </>
              ) : (
                <>
                  <Heading fontSize={24} mt={3} mb={2} textAlign="center">
                    Protect your account
                  </Heading>
                  <Text fontSize={13} textAlign="center" opacity={0.7} mb={4}>
                    Last step — these are separate from your password.
                  </Text>
                  <StepIndicator />
                  <Box w="full" bg="#0e0e0e10" p={3} mb={4} fontSize={12}>
                    <Text fontWeight={700} mb={1}>
                      <InfoOutlineIcon mr={2} />
                      Almost done
                      {inputVal.username ? `, ${inputVal.username}` : ''}!
                    </Text>
                    <Text>
                      Write these down somewhere safe. You will be asked for
                      them again whenever you change your password, username or
                      email address.
                    </Text>
                  </Box>
                  <LabeledInput
                    label="PIN CODE"
                    hint="A separate code that guards your account. You will be asked for it to change your password, username or email. Do not reuse your password."
                    placeholder="Your transaction pin code"
                    maxLength={11}
                    autoComplete="off"
                    isSecret
                    {...fieldProps('pinCode', handleSubmit)}
                  />
                  <LabeledInput
                    label="CONFIRM PIN CODE"
                    hint="Type the same pin code again."
                    placeholder="Repeat your pin code"
                    maxLength={11}
                    autoComplete="off"
                    isSecret
                    {...fieldProps('confirmPinCode', handleSubmit)}
                  />
                  <LabeledInput
                    label="SECURITY KEY"
                    hint="Your recovery answer — support asks for this to prove the account is yours. Keep it private."
                    placeholder="Your recovery key"
                    maxLength={11}
                    autoComplete="off"
                    isSecret
                    {...fieldProps('securityKey', handleSubmit)}
                  />
                  <LabeledInput
                    label="REFERRAL CODE"
                    hint="Invited by a friend? Enter their referral code so they get credit. Leave this empty if you have none."
                    placeholder="Friend's referral code"
                    maxLength={15}
                    isOptional
                    {...fieldProps('referralCode', handleSubmit)}
                  />
                </>
              )}
            </Flex>

            {/* Kept outside the scrolling area so the button the user needs
                next is always visible, even on short screens. */}
            <Box
              w="full"
              textAlign="center"
              fontSize={14}
              borderTop="1px solid #0e0e0e25"
              pt={4}
              mt={2}
            >
              {page === 0 ? (
                <Button onClick={goToNextStep} variant="maroon" mb={3}>
                  {'> NEXT'}
                </Button>
              ) : (
                <Stack
                  direction={['column', 'row']}
                  justify="center"
                  align="center"
                  mb={3}
                >
                  <Button
                    onClick={() => setPage(0)}
                    variant="outline"
                    isDisabled={isLoading}
                  >
                    {'> BACK'}
                  </Button>
                  <Button onClick={handleSubmit} variant="maroon">
                    {isLoading ? <Spinner /> : '> CREATE ACCOUNT'}
                  </Button>
                </Stack>
              )}
              <Text>
                Already have an account?{' '}
                <Link as={RouterLink} to="/login">
                  {'> Log in'}
                </Link>
              </Text>
            </Box>
          </StyledModalContent>
        </Modal>
      </Page>
      <ReCAPTCHA
        sitekey={RECAPTCHA_SITE_KEY}
        size="invisible"
        ref={recaptchaRef}
      />
    </>
  )
}

export default Register
